import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, Mail, AlertCircle } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0C10] flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-white/20 border-t-[#4A90D9] rounded-full animate-spin" />
      </div>
    );
  }

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0A0C10] relative overflow-hidden"
      data-testid="admin-login-page"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.06]"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/904593/pexels-photo-904593.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0C10] via-[#0A0C10]/95 to-[#0A0C10]/90" />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-[#11141A] border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h1
              className="font-heading text-3xl font-bold text-white tracking-tight"
              data-testid="login-logo"
            >
              DiNA
              <span style={{ color: "var(--primary-color, #4A90D9)" }}>
                XLP
              </span>
            </h1>
            <p className="text-[#6E7787] text-sm mt-2 font-body">
              Admin Portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-[#A1A9B8] font-body text-sm"
              >
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@dinaxlp.com"
                  required
                  data-testid="login-email-input"
                  className="pl-10 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] focus:ring-1 focus:ring-[var(--primary-color,#4A90D9)] h-11"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-[#A1A9B8] font-body text-sm"
              >
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  data-testid="login-password-input"
                  className="pl-10 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] focus:ring-1 focus:ring-[var(--primary-color,#4A90D9)] h-11"
                />
              </div>
            </div>

            {error && (
              <div
                className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
                data-testid="login-error-message"
              >
                <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                <p className="text-red-400 text-sm font-body">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full h-11 font-body font-medium text-white rounded-xl transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {submitting ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
